/**
 * FreshCards Kanban View — v2 with @dnd-kit
 * Replaces the broken HTML5 drag-and-drop (which was using
 * setDragImage / dataTransfer patterns that don't survive React's
 * synthetic events across the strict mode boundary).
 *
 * v2 features:
 * - @dnd-kit/core for accessible, keyboard-friendly drag-and-drop
 * - DragOverlay for visual feedback while dragging
 * - Column-level drop zones (drop on the column body, not on a card)
 * - "Add item" inline button on each column header
 * - Group-by property persists in DatabaseView state (out of scope here)
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Paper,
  Card,
  Text,
  Group,
  ActionIcon,
  ScrollArea,
  Stack,
  Button,
  Modal,
  TextInput,
  Box,
  Flex,
  Menu,
} from '@mantine/core';
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
  IconGripVertical,
  IconDots,
  IconEdit,
  IconTrash,
  IconCopy,
  IconPlus,
} from '@tabler/icons-react';
import { DatabaseItem, Database, PropertyDefinition } from '../../../types/models/Database';
import { groupItems, getColumns, normaliseTagKeys, type GroupByMode } from '../database/viewConfig';
// F 506 — THE GROUPED BOARD. The date modes are a DIFFERENT BOARD, not a
// variant of the property board, so it is its own component.
import { GroupedBoardView } from '../database/GroupedBoardView';
import { isDateGroupByMode } from '../../../core/views/groupRecords';
import { resolveCover } from '../database/coverResolver';

interface KanbanViewProps {
  database: Database;
  items: DatabaseItem[];
  /**
   * Group-by mode (V5.1 view config). When omitted, falls back to
   * the legacy `groupByPropertyId` prop for backward compatibility.
   */
  groupBy?: GroupByMode;
  /**
   * F 509 — WHICH DATE PROPERTY THE BOARD GROUPS BY.
   *
   * ⇒⇒□ ⇒⇒□ **⇒⇒□ THE WHOLE POINT OF THE `'date'` MODE** — AND THE
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ SCHEMA COMES WITH IT, BECAUSE WITHOUT THE SCHEMA THE
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ GROUPER CANNOT KNOW A PROPERTY IS A DATE.
   */
  groupByField?: string;
  /** F 509 — HOW COARSELY THE BOARD BUCKETS THE DATE. */
  groupGranularity?: import('../../../core/views/groupRecords').GroupGranularity;
  /** @deprecated use `groupBy` instead. Kept for backward compat. */
  groupByPropertyId?: string;
  onItemSelect?: (item: DatabaseItem) => void;
  onItemCreate?: (item: DatabaseItem) => void;
  /**
   * F 509 - CREATE A CARD FROM A DATE COLUMN.
   *
   * The date board is a first-class board, and it must be able to receive
   * a card. `GroupedBoardView` reports the column and the date it stands
   * for; the surface decides what a date means.
   */
  onItemCreateInColumn?: (column: { id: string; date?: string }) => void;
  onItemUpdate?: (item: DatabaseItem) => void;
  onItemDelete?: (itemId: string) => void;
  onItemDuplicate?: (item: DatabaseItem) => void;
  /**
   * F 506 — THE BOARD'S PRESENTATION TOGGLES.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THESE ARRIVE AS PROPS, NOT AS
   * `useState` INSIDE THIS COMPONENT.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
   * **⇒⇒□ THE STATE LIVES ON THE VIEW CONFIG AND IS PERSISTED THROUGH THE
   * GRAPH; A LOCAL `useState` HERE WOULD RESET ON RELOAD AND THE
   * PUSH/PULL TEST WOULD FAIL.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
   */
  wrapContent?: boolean;
  colorColumns?: boolean;
  showIcons?: boolean;
}

// F 85 — G-1: prop type now exported as part of the public view API.
export type { KanbanViewProps };

interface KanbanColumn {
  id: string;
  name: string;
  color: string;
  items: DatabaseItem[];
}

// V4 fragment: kanban-view
// Behavior: drag a card between columns → updates the item's group-by property value
//
// Card covers (NEW): each kanban card now renders an optional cover
// image — either a remote URL stored on the item's 'cover' property,
// or a stable per-item gradient. The cover sits above the card body
// (title + date) and is clipped to the card's rounded top corners.
// The same cover strategy is used in GalleryView.

const KANBAN_GRADIENT_COVERS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)',
  'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
  'linear-gradient(135deg, #cd9cf2 0%, #f6f3ff 100%)',
  'linear-gradient(135deg, #fddb92 0%, #d1fdff 100%)',
];

function getGradientCover(idOrTitle: string): string {
  const hash = idOrTitle.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return KANBAN_GRADIENT_COVERS[hash % KANBAN_GRADIENT_COVERS.length];
}
export const KanbanView: React.FC<KanbanViewProps> = ({
  database,
  items,
  groupBy,
  groupByField,
  groupGranularity,
  groupByPropertyId,
  onItemSelect,
  onItemCreate,
  onItemCreateInColumn,
  onItemUpdate,
  onItemDelete,
  onItemDuplicate,
  // F 506 — the board's presentation toggles.
  wrapContent = false,
  colorColumns = true,
  showIcons = true,
}) => {
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [newItemTitle, setNewItemTitle] = useState('');
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<DatabaseItem | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  // Map a group-by mode to the underlying property id (and its value type).
  // Used by drag-and-drop and add-item to update the right property.
  const propertyForGroup = (mode: GroupByMode | undefined): string | undefined => {
    switch (mode) {
      case 'status':   return 'status';
      case 'priority': return 'priority';
      case 'assignee': return 'assignee';
      case 'tag':      return 'tag';
      case 'due-week': return 'date';
      default:         return undefined;
    }
  };

  // Build kanban columns using the view-config groupItems selector.
  // Falls back to the legacy groupByPropertyId path for backward compat.
  const columns: KanbanColumn[] = useMemo(() => {
    // V5.1 path: use the view config groupBy mode
    if (groupBy) {
      if (groupBy === 'none') {
        return [{ id: 'all', name: 'All', color: '#94a3b8', items }];
      }
      const groups = groupItems(items, groupBy);
      const cols = getColumns(groups);
      return cols.map(c => ({
        id: c.id,
        name: c.name,
        color: c.color,
        items: groups.get(c.id) ?? [],
      }));
    }

    // Legacy path: group by a property id (status_active, status_completed, etc.)
    if (!groupByPropertyId) {
      return [
        { id: 'active', name: 'Active', color: '#52b788', items },
        { id: 'completed', name: 'Completed', color: '#588b8b', items: [] },
        { id: 'archived', name: 'Archived', color: '#adb5bd', items: [] },
      ];
    }
    const property = database.schema.properties.find(p => p.id === groupByPropertyId);
    if (!property) {
      return [{ id: 'ungrouped', name: 'Ungrouped', color: '#adb5bd', items }];
    }
    const columnMap = new Map<string, { name: string; color: string; items: DatabaseItem[] }>();
    (property.options || property.statusOptions || []).forEach(opt => {
      columnMap.set(opt.id, { name: opt.name, color: opt.color, items: [] });
    });
    items.forEach(item => {
      const prop = item.properties.find(p => p.propertyId === groupByPropertyId);
      if (prop) {
        const valueKey = typeof prop.value === 'object' && prop.value !== null
          ? (prop.value as any).id
          : String(prop.value);
        const col = columnMap.get(valueKey as string);
        if (col) col.items.push(item);
      }
    });
    return Array.from(columnMap.entries()).map(([id, data]) => ({ id, ...data }));
  }, [database.schema.properties, groupBy, groupByPropertyId, items]);

  // dnd-kit handlers
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;
    const itemId = String(active.id);
    const targetColumnId = String(over.id);

    const item = items.find(i => i.id === itemId);
    if (!item) return;

    const column = columns.find(c => c.id === targetColumnId);
    if (!column) return;

    // Determine which property to update based on the active group-by mode
    const activeGroupBy: GroupByMode | undefined = groupBy ?? (
      groupByPropertyId === 'status' ? 'status' : undefined
    );
    const propId = propertyForGroup(activeGroupBy);
    if (!propId) return; // 'none' mode or no grouping → no-op

    // Build the new property value based on the group-by mode
    let newValue: any = column.id;
    if (activeGroupBy === 'status' || activeGroupBy === 'priority' || activeGroupBy === 'assignee') {
      newValue = { id: column.id, name: column.name, color: column.color, state: column.id };
    } else if (activeGroupBy === 'tag') {
      // F 367: the existing value may be `string[]` OR `SelectOption[]`
      // (PropertyValue.value is `unknown`, and every real multi_select adapter
      // writes objects). Toggle by the same normalised key groupItems uses,
      // and WRITE BACK THE SAME SHAPE we read — dragging a card must not
      // silently rewrite a user's option objects as bare strings.
      const tagKey = column.id.startsWith('tag_') ? column.id.slice(4) : column.name;
      const prop = item.properties.find(p => p.propertyId === 'tag');
      const existing = normaliseTagKeys(prop?.value);
      const isObjectShape = Array.isArray(prop?.value) && prop!.value.some(
        (e: unknown) => e !== null && typeof e === 'object',
      );
      const remaining = existing.filter(t => t.key !== tagKey);
      if (remaining.length === existing.length) {
        // Not present -> add it, preserving the observed shape.
        const added = [...existing, { key: tagKey, name: column.name }];
        newValue = isObjectShape
          ? added.map(t => ({ id: t.key, name: t.name, color: column.color ?? '#a855f7' }))
          : added.map(t => t.name);
      } else {
        newValue = isObjectShape
          ? remaining.map(t => ({ id: t.key, name: t.name, color: '#a855f7' }))
          : remaining.map(t => t.name);
      }
    } else if (activeGroupBy === 'due-week') {
      // Map the column to a representative date
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      if (column.id === 'due_overdue') {
        const d = new Date(startOfWeek); d.setDate(d.getDate() - 1);
        newValue = d.toISOString().split('T')[0];
      } else if (column.id === 'due_thisweek') {
        newValue = startOfWeek.toISOString().split('T')[0];
      } else if (column.id === 'due_nextweek') {
        const d = new Date(startOfWeek); d.setDate(d.getDate() + 7);
        newValue = d.toISOString().split('T')[0];
      } else {
        const d = new Date(startOfWeek); d.setDate(d.getDate() + 30);
        newValue = d.toISOString().split('T')[0];
      }
    }

    const updatedItem: DatabaseItem = {
      ...item,
      properties: item.properties.map(p =>
        p.propertyId === propId ? { ...p, value: newValue } : p
      ),
      updatedAt: new Date(),
    };
    onItemUpdate?.(updatedItem);
  }, [items, columns, groupBy, groupByPropertyId, onItemUpdate]);

  // Item-level handlers
  const handleAddItemToColumn = (columnId: string) => {
    setSelectedColumn(columnId);
    setIsAddingItem(true);
    setNewItemTitle('');
  };

  const handleCreateItem = () => {
    if (!newItemTitle.trim() || !selectedColumn) return;
    const column = columns.find(c => c.id === selectedColumn);
    if (!column) return;

    // Build the new property value based on the active group-by mode
    const activeGroupBy: GroupByMode | undefined = groupBy ?? (
      groupByPropertyId === 'status' ? 'status' : undefined
    );
    const propId = propertyForGroup(activeGroupBy);
    let newValue: any = column.id;
    if (activeGroupBy === 'status' || activeGroupBy === 'priority' || activeGroupBy === 'assignee') {
      newValue = { id: column.id, name: column.name, color: column.color, state: column.id };
    } else if (activeGroupBy === 'tag') {
      newValue = [column.name];
    } else if (activeGroupBy === 'due-week') {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      if (column.id === 'due_overdue') {
        const d = new Date(startOfWeek); d.setDate(d.getDate() - 1);
        newValue = d.toISOString().split('T')[0];
      } else if (column.id === 'due_thisweek') {
        newValue = startOfWeek.toISOString().split('T')[0];
      } else if (column.id === 'due_nextweek') {
        const d = new Date(startOfWeek); d.setDate(d.getDate() + 7);
        newValue = d.toISOString().split('T')[0];
      } else {
        const d = new Date(startOfWeek); d.setDate(d.getDate() + 30);
        newValue = d.toISOString().split('T')[0];
      }
    }

    const newItem: DatabaseItem = {
      id: `item_${Date.now()}`,
      databaseId: database.id,
      title: newItemTitle,
      properties: propId ? [{ propertyId: propId, value: newValue }] : [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    onItemCreate?.(newItem);
    setIsAddingItem(false);
    setNewItemTitle('');
    setSelectedColumn(null);
  };

  const handleDeleteItem = (itemId: string) => {
    if (confirm('Delete this item?')) {
      onItemDelete?.(itemId);
    }
  };

  const activeItem = activeId ? items.find(i => i.id === activeId) : null;

  /**
   * F 506 — THE DATE BOARD IS A DIFFERENT BOARD.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ A DATE-GROUPED BOARD HAS NO FIXED
   * SET OF COLUMNS.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE PROPERTY BOARD
   * HAS FIVE STATUS COLUMNS, FOUR PRIORITY COLUMNS, FIVE DUE-WEEK
   * COLUMNS — ALL DECLARED UP FRONT, ALL ALWAYS PRESENT, AND A CARD
   * WITH NO STATUS LANDS IN "BACKLOG" BECAUSE THE COLUMN LIST SAYS SO.**
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ A DATE BOARD HAS WHATEVER DAYS THE
   * RECORDS FALL ON, PLUS "UNCATEGORIZED" FOR THE ONES WITH NO DATE —
   * AND THAT SET CHANGES AS THE RECORDS CHANGE.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
   * **⇒⇒□ SO THE DATE BOARD CANNOT BE THE PROPERTY BOARD WITH DIFFERENT
   * COLUMN DATA — IT IS A DIFFERENT COMPONENT.**
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ AND THE DRAG-AND-DROP IS DELIBERATELY NOT
   * CARRIED OVER. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE PROPERTY BOARD'S
   * DRAG WRITES A PROPERTY VALUE — IT MOVES A CARD BETWEEN STATUSES.**
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ A DATE GROUP IS DERIVED FROM
   * `createdAt`, WHICH IS NOT A PROPERTY AND IS NOT WRITABLE — MOVING A
   * CARD TO ANOTHER DAY COLUMN WOULD HAVE TO REWRITE ITS CREATION DATE,
   * WHICH IS NOT WHAT THE OPERATOR MEANT.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
   */
  if (groupBy && isDateGroupByMode(groupBy)) {
    return (
      <div className="kanban-view-wrapper" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <GroupedBoardView
          records={items}
          groupBy={groupBy}
          groupByField={groupByField}
          groupGranularity={groupGranularity}
          schema={database.schema.properties}
          wrapContent={wrapContent}
          colorColumns={colorColumns}
          showIcons={showIcons}
          onItemClick={onItemSelect}
          onItemCreate={onItemCreateInColumn}
        />
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="kanban-view-wrapper" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <ScrollArea
          type="auto"
          scrollbarSize={6}
          className="fc-kanban-scroll"
          style={{ width: '100%', flex: 1, minHeight: 0 }}
        >
          <Flex
            gap="md"
            p="md"
            className="fc-kanban-track"
            style={{ minWidth: 'fit-content' }}
          >
            {columns.map(column => (
              <KanbanColumnView
                key={column.id}
                column={column}
                database={database}
                onAddItem={() => handleAddItemToColumn(column.id)}
                onItemSelect={onItemSelect}
                onItemEdit={setEditingItem}
                onItemDelete={handleDeleteItem}
                onItemDuplicate={onItemDuplicate}
              />
            ))}
          </Flex>
        </ScrollArea>

        {/* Mobile-only: column page indicator dots so the user knows
            they can swipe. Hidden on desktop where all columns fit. */}
        <ColumnIndicator count={columns.length} />

        <DragOverlay>
          {activeItem ? <KanbanCardPreview item={activeItem} /> : null}
        </DragOverlay>

        {/* Add Item Modal */}
        <Modal
          opened={isAddingItem}
          onClose={() => { setIsAddingItem(false); setSelectedColumn(null); }}
          title={`Add to ${columns.find(c => c.id === selectedColumn)?.name ?? 'column'}`}
          size="sm"
        >
          <Stack>
            <TextInput
              label="Title"
              placeholder="Enter item title"
              value={newItemTitle}
              onChange={e => setNewItemTitle(e.currentTarget.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCreateItem(); }}
              autoFocus
            />
            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" onClick={() => { setIsAddingItem(false); setSelectedColumn(null); }}>
                Cancel
              </Button>
              <Button onClick={handleCreateItem} disabled={!newItemTitle.trim()}>
                Create
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Edit Item Modal */}
        <Modal
          opened={!!editingItem}
          onClose={() => setEditingItem(null)}
          title="Edit item"
          size="sm"
        >
          {editingItem && (
            <Stack>
              <TextInput
                label="Title"
                value={editingItem.title}
                onChange={e => setEditingItem({ ...editingItem, title: e.currentTarget.value })}
              />
              <Group justify="flex-end" gap="sm">
                <Button variant="subtle" onClick={() => setEditingItem(null)}>Cancel</Button>
                <Button onClick={() => { onItemUpdate?.(editingItem); setEditingItem(null); }}>Save</Button>
              </Group>
            </Stack>
          )}
        </Modal>
      </div>
    </DndContext>
  );
};

/** Single column — droppable zone, holds the items list. */
const KanbanColumnView: React.FC<{
  column: KanbanColumn;
  database: Database;
  onAddItem: () => void;
  onItemSelect?: (item: DatabaseItem) => void;
  onItemEdit: (item: DatabaseItem) => void;
  onItemDelete: (itemId: string) => void;
  onItemDuplicate?: (item: DatabaseItem) => void;
}> = ({ column, database, onAddItem, onItemSelect, onItemEdit, onItemDelete, onItemDuplicate }) => {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });

  return (
    <Paper
      p="md"
      radius="md"
      ref={setNodeRef}
      className="fc-kanban-column"
      style={{
        width: 'var(--fc-kanban-col-width, 320px)',
        // No maxHeight here — the column should grow with its content.
        // The board row (parent) has its own height/overflow that
        // provides the viewport-bounded window.
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: isOver
          ? 'var(--fc-bg-secondary, #f7f6f3)'
          : 'var(--fc-bg-tertiary, #f1f1ef)',
        outline: isOver ? '2px dashed var(--fc-accent-primary, #475569)' : 'none',
        transition: 'all 150ms ease',
        flexShrink: 0,
      }}
    >
      {/* Column header */}
      <Group justify="space-between" mb="md" align="center">
        <Group gap="sm" align="center">
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: 4,
              backgroundColor: column.color,
            }}
          />
          <Stack gap={0}>
            <Text fw={600} size="sm">{column.name}</Text>
            <Text size="xs" c="dimmed">{column.items.length} items</Text>
          </Stack>
        </Group>
      </Group>

      {/* Items */}
      <Stack
        gap="sm"
        style={{ flex: 1, minHeight: 200, overflowY: 'auto' }}
      >
        {column.items.map(item => (
          <DraggableCard
            key={item.id}
            item={item}
            database={database}
            onClick={() => onItemSelect?.(item)}
            onEdit={() => onItemEdit(item)}
            onDelete={() => onItemDelete(item.id)}
            onDuplicate={() => onItemDuplicate?.(item)}
          />
        ))}
        {column.items.length === 0 && (
          <Box
            style={{
              textAlign: 'center',
              padding: '20px',
              color: 'var(--fc-text-muted, #9ca3af)',
              minHeight: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px dashed var(--fc-border-secondary, #d4d4d1)',
              borderRadius: 8,
            }}
          >
            <Text size="sm" c="dimmed">Drag cards here</Text>
          </Box>
        )}
      </Stack>

      {/* Add button */}
      <Button
        variant="subtle"
        size="sm"
        fullWidth
        mt="md"
        leftSection={<IconPlus size={14} />}
        onClick={onAddItem}
        style={{ color: 'var(--fc-text-secondary, #4b5563)' }}
      >
        Add item
      </Button>
    </Paper>
  );
};

/** Draggable card — wraps Mantine Card with dnd-kit's useDraggable.
 * The whole card is draggable (not just the grip icon) so Playwright
 * tests and touch users can drag from anywhere on the card. */
const DraggableCard: React.FC<{
  item: DatabaseItem;
  database: Database;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}> = ({ item, database, onClick, onEdit, onDelete, onDuplicate }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: item.id });
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
    cursor: 'grab',
    touchAction: 'none',
  };

  // Resolve the cover using the shared resolver (cover prop → image
  // prop → deterministic gradient).
  const coverUrl = resolveCover(item, database);
  const isRemoteCover = coverUrl && /^https?:\/\//.test(coverUrl);

  return (
    <Card
      ref={setNodeRef}
      style={{
        ...style,
        backgroundColor: 'var(--mantine-color-body)',
        color: 'var(--mantine-color-text)',
        overflow: 'hidden',
        padding: 0,
      }}
      withBorder
      radius="md"
      onClick={onClick}
      {...attributes}
      {...listeners}
    >
      {/* Cover — gradient or remote image. The aspect-ratio is enforced
          via the padding-top hack (16:9 → 56.25% padding-top) because
          CSS aspect-ratio can collapse inside flex parents in some
          browsers. The gradient/img is then absolutely positioned
          inside the padded wrapper. */}
      {coverUrl && (
        <div style={{
          position: 'relative',
          width: '100%',
          paddingTop: '56.25%',
          overflow: 'hidden',
        }}>
          {isRemoteCover ? (
            <img src={coverUrl} alt={item.title} loading="lazy"
                 style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                 onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
          ) : (
            <div style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', background: coverUrl }} />
          )}
        </div>
      )}

      <Box p="sm">
      <Group justify="space-between" align="flex-start" mb="xs" wrap="nowrap">
        <Group gap={4} align="flex-start" style={{ flex: 1, minWidth: 0 }}>
          <ActionIcon
            size="xs"
            variant="subtle"
            color="gray"
            component="div"
            style={{ cursor: 'grab' }}
          >
            <IconGripVertical size={14} />
          </ActionIcon>
          <Text size="sm" fw={500} lineClamp={2} style={{ flex: 1 }}>
            {item.title}
          </Text>
        </Group>
        <Menu shadow="md" position="bottom-end">
          <Menu.Target>
            <ActionIcon size="xs" variant="subtle" color="gray" onClick={e => e.stopPropagation()}>
              <IconDots size={14} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item leftSection={<IconEdit size={14} />} onClick={(e) => { e.stopPropagation(); onEdit(); }}>
              Edit
            </Menu.Item>
            <Menu.Item leftSection={<IconCopy size={14} />} onClick={(e) => { e.stopPropagation(); onDuplicate(); }}>
              Duplicate
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
              Delete
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
      <Text size="xs" c="dimmed">
        {new Date(item.createdAt).toLocaleDateString()}
      </Text>
      </Box>
    </Card>
  );
};

/** Drag-overlay preview — the card shown while dragging. */
const KanbanCardPreview: React.FC<{ item: DatabaseItem }> = ({ item }) => (
  <Card withBorder radius="md" padding="sm" style={{
    backgroundColor: 'var(--mantine-color-body)',
    color: 'var(--mantine-color-text)',
    boxShadow: 'var(--fc-shadow-lg, 0 8px 16px rgba(0,0,0,0.12))',
    cursor: 'grabbing',
    width: 280,
  }}>
    <Text size="sm" fw={500} lineClamp={2}>{item.title}</Text>
    <Text size="xs" c="dimmed" mt={4}>{new Date(item.createdAt).toLocaleDateString()}</Text>
  </Card>
);

/**
 * Column page-indicator dots for mobile kanban. Renders one dot per column
 * and highlights the one closest to the scroll viewport. Tapping a dot
 * scrolls to that column. Hidden on desktop where all columns fit.
 */
const ColumnIndicator: React.FC<{ count: number }> = ({ count }) => {
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrapper = ref.current?.closest('.kanban-view-wrapper');
    if (!wrapper) return;
    const scroller = wrapper.querySelector('.fc-kanban-scroll [data-mantine-scroll-area-viewport], .fc-kanban-scroll > div') as HTMLElement | null;
    if (!scroller) return;
    const onScroll = () => {
      const colWidth = scroller.scrollWidth / Math.max(count, 1);
      const idx = Math.round(scroller.scrollLeft / colWidth);
      setActive(Math.min(idx, count - 1));
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [count]);

  if (count <= 1) return null;
  return (
    <div ref={ref} className="fc-kanban-indicator" aria-label={`Column ${active + 1} of ${count}`}>
      {Array.from({ length: count }).map((_, i) => (
        <span key={i} className={`fc-kanban-dot ${i === active ? 'active' : ''}`} />
      ))}
    </div>
  );
};
